import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Grid, Skeleton, Typography } from '@mui/material';
import { StarRounded, LocationOn } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../components/SectionContainer';
import RevealOnScroll from '../components/RevealOnScroll';

/**
 * Renders a filled-star row based on average_overall_rating (0–5).
 */
function StarRow({ rating, totalRatings }) {
  if (!totalRatings) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        New on BML
      </Typography>
    );
  }

  const filled = Math.round(rating || 0);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <StarRounded
          key={i}
          sx={{
            fontSize: 16,
            color: i < filled ? '#F7B32B' : 'rgba(0,0,0,0.15)',
          }}
          aria-hidden="true"
        />
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
        ({totalRatings})
      </Typography>
    </Box>
  );
}

StarRow.propTypes = {
  rating: PropTypes.number,
  totalRatings: PropTypes.number,
};

StarRow.defaultProps = {
  rating: 0,
  totalRatings: 0,
};

/**
 * Single library card — non-clickable trust signal.
 */
function LibraryCard({ library }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;

  const rawImage = library.gallery && library.gallery.length > 0 ? library.gallery[0] : null;
  const imageUrl = rawImage && rawImage.startsWith('/uploads/')
    ? `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${rawImage}`
    : rawImage;
  const cardBg = isDark ? 'rgba(27,54,93,0.5)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.07)'
    : '1px solid rgba(27,54,93,0.06)';

  const amenities = Array.isArray(library.amenities) ? library.amenities.slice(0, 3) : [];

  return (
    <Box
      sx={{
        borderRadius: '20px',
        background: cardBg,
        border: cardBorder,
        boxShadow: isDark
          ? '0 4px 20px rgba(0,0,0,0.2)'
          : '0 4px 20px rgba(27,54,93,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: isDark
            ? '0 20px 40px -8px rgba(0,0,0,0.45), 0 0 0 1px rgba(242,101,34,0.2)'
            : '0 20px 40px -16px rgba(242,101,34,0.25), 0 0 0 1px rgba(242,101,34,0.12)',
        },
      }}
    >
      {/* Hero image */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '62.5%', // 16:10 aspect ratio
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={`${library.name} library`}
            loading="lazy"
            decoding="async"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${theme.palette.brand.navy.dark}, ${theme.palette.brand.navy.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: 'rgba(255,255,255,0.15)',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                userSelect: 'none',
              }}
            >
              BML
            </Typography>
          </Box>
        )}

        {/* Top-rated badge */}
        {library.top_rated && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              px: 1.25,
              py: 0.4,
              borderRadius: '999px',
              background: `linear-gradient(135deg, ${orange}, ${theme.palette.brand.golden.main})`,
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              boxShadow: '0 2px 8px rgba(242,101,34,0.4)',
            }}
            aria-label="Top rated library"
          >
            Top Rated
          </Box>
        )}
      </Box>

      {/* Card body */}
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1 }}>
        {/* Name */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '0.975rem',
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {library.name}
        </Typography>

        {/* City */}
        {library.city && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {library.city}
              {library.locality ? `, ${library.locality}` : ''}
            </Typography>
          </Box>
        )}

        {/* Star rating */}
        <StarRow
          rating={library.average_overall_rating}
          totalRatings={library.total_ratings}
        />

        {/* Amenity chips */}
        {amenities.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {amenities.map((amenity) => (
              <Chip
                key={amenity}
                label={amenity}
                size="small"
                sx={{
                  fontSize: '0.68rem',
                  height: 22,
                  bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(27,54,93,0.06)',
                  color: 'text.secondary',
                  border: 'none',
                }}
              />
            ))}
          </Box>
        )}

        {/* Price */}
        {library.minPrice != null && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mt: 'auto',
              pt: 0.5,
            }}
          >
            Starts at{' '}
            <Box component="span" sx={{ fontWeight: 700, color: orange }}>
              ₹{library.minPrice}
            </Box>
            /day
          </Typography>
        )}
      </Box>
    </Box>
  );
}

LibraryCard.propTypes = {
  library: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    city: PropTypes.string,
    locality: PropTypes.string,
    gallery: PropTypes.arrayOf(PropTypes.string),
    amenities: PropTypes.arrayOf(PropTypes.string),
    top_rated: PropTypes.bool,
    average_overall_rating: PropTypes.number,
    total_ratings: PropTypes.number,
    minPrice: PropTypes.number,
  }).isRequired,
};

/**
 * Skeleton placeholder for loading state.
 */
function LibraryCardSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Skeleton variant="rectangular" sx={{ width: '100%', paddingTop: '62.5%', height: 0 }} />
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Skeleton variant="text" width="70%" height={24} />
        <Skeleton variant="text" width="45%" height={16} />
        <Skeleton variant="text" width="55%" height={16} />
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Skeleton variant="rounded" width={60} height={22} />
          <Skeleton variant="rounded" width={70} height={22} />
          <Skeleton variant="rounded" width={55} height={22} />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Featured libraries section — replaces fake testimonials.
 */
function FeaturedLibrariesSection({ libraries, loading }) {
  if (!loading && (!libraries || libraries.length === 0)) {
    return null;
  }

  return (
    <SectionContainer
      eyebrow="Real libraries. Real partners."
      title="Libraries already thriving on BookMyLibrary."
      subtitle="Here's a glimpse of the libraries that trust us with their seats, their payments, and their students."
    >
      <Grid container spacing={3}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={`skeleton-${i}`}>
                <LibraryCardSkeleton />
              </Grid>
            ))
          : libraries.map((library, index) => (
              <Grid item xs={12} sm={6} md={4} key={library.id || index}>
                <RevealOnScroll delay={Math.min(index * 0.07, 0.5)}>
                  <LibraryCard library={library} />
                </RevealOnScroll>
              </Grid>
            ))}
      </Grid>
    </SectionContainer>
  );
}

FeaturedLibrariesSection.propTypes = {
  libraries: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default FeaturedLibrariesSection;
